import React from 'react';
import { cn } from '@/utils/cn';
import { Alert } from '@/components/ui/Alert';
import { Input } from '@/components/ui/Input';
import type {
  MetaAdSetCreateStatus,
  MetaBillingEvent,
  MetaCampaignObjective,
  MetaOptimizationGoal,
} from '@/services/api/metaAds';
import {
  OBJECTIVE_TO_OPTIMIZATION_GOALS,
  OPTIMIZATION_GOAL_TO_BILLING_EVENTS,
} from '../constants/metaAdSetMatrix';

interface AdSetBasicsStepProps {
  /** Parent campaign objective. Filters which optimization goals are
   *  offered. Backend re-validates after lookup. */
  parentObjective: MetaCampaignObjective;
  adSetName: string;
  optimizationGoal: MetaOptimizationGoal;
  billingEvent: MetaBillingEvent;
  status: MetaAdSetCreateStatus;
  /** Set when the parent is a CBO campaign AND already has at least one
   *  ad set. Meta requires every ad set in a CBO campaign to share the
   *  same `optimization_goal` — when this is non-null, the dropdown is
   *  rendered disabled and pre-set to the inherited value. */
  inheritedOptimizationGoal?: MetaOptimizationGoal | null;
  /** Set when the parent is CBO and this is the FIRST ad set under it.
   *  Dropdown is open but the wizard surfaces a notice that this choice
   *  will lock the goal for every future ad set in this campaign. */
  isFirstAdSetInCbo?: boolean;
  onChangeName: (name: string) => void;
  onChangeOptimizationGoal: (goal: MetaOptimizationGoal) => void;
  onChangeBillingEvent: (event: MetaBillingEvent) => void;
  onChangeStatus: (status: MetaAdSetCreateStatus) => void;
  errors?: {
    name?: string;
    optimizationGoal?: string;
    billingEvent?: string;
  };
}

const OPTIMIZATION_GOAL_LABELS: Record<MetaOptimizationGoal, string> = {
  REACH: 'Reach',
  IMPRESSIONS: 'Impressions',
  AD_RECALL_LIFT: 'Ad recall lift',
  LINK_CLICKS: 'Link clicks',
  LANDING_PAGE_VIEWS: 'Landing page views',
  POST_ENGAGEMENT: 'Post engagement',
  PAGE_LIKES: 'Page likes',
  EVENT_RESPONSES: 'Event responses',
  THRUPLAY: 'ThruPlay (video)',
  VIDEO_VIEWS: 'Video views',
  OFFSITE_CONVERSIONS: 'Conversions (pixel)',
  VALUE: 'Conversion value',
  LEAD_GENERATION: 'Lead generation',
  QUALITY_LEAD: 'Quality lead',
  APP_INSTALLS: 'App installs',
  CONVERSATIONS: 'Messenger conversations',
};

const BILLING_EVENT_LABELS: Record<MetaBillingEvent, string> = {
  IMPRESSIONS: 'Per impression',
  LINK_CLICKS: 'Per link click',
  THRUPLAY: 'Per ThruPlay',
  POST_ENGAGEMENT: 'Per engagement',
  PAGE_LIKES: 'Per page like',
  APP_INSTALLS: 'Per app install',
};

/**
 * Step 1 — basics: name, optimization goal, billing event, status.
 *
 * Optimization-goal options narrow on the parent campaign's objective
 * (via `OBJECTIVE_TO_OPTIMIZATION_GOALS`). Billing-event options narrow
 * on the selected goal (via `OPTIMIZATION_GOAL_TO_BILLING_EVENTS`).
 * Backend re-validates after parent-campaign lookup.
 *
 * Structural analog of Google's `AdGroupBasicsStep` — same single-column
 * layout, same inline-error pattern, same labeled inputs.
 */
export const AdSetBasicsStep: React.FC<AdSetBasicsStepProps> = ({
  parentObjective,
  adSetName,
  optimizationGoal,
  billingEvent,
  status,
  inheritedOptimizationGoal = null,
  isFirstAdSetInCbo = false,
  onChangeName,
  onChangeOptimizationGoal,
  onChangeBillingEvent,
  onChangeStatus,
  errors,
}) => {
  const goalLocked = inheritedOptimizationGoal !== null;
  const allowedGoals =
    OBJECTIVE_TO_OPTIMIZATION_GOALS[parentObjective] ?? [];
  const allowedBillingEvents =
    OPTIMIZATION_GOAL_TO_BILLING_EVENTS[optimizationGoal] ?? [];

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Ad set basics</h3>
        <p className="text-sm text-gray-600">
          Name your ad set and tell Meta what to optimize for.
        </p>
      </div>

      {/* First-ad-set-in-CBO warning. Promoted to a yellow Alert from
          the small grey hint under the dropdown because users were
          missing the lock-in implication. The dropdown hint stays
          (says "Options narrowed by parent objective...") for the
          non-warning context. */}
      {isFirstAdSetInCbo && (
        <Alert variant="warning">
          <p className="text-sm font-medium">
            First ad set under a CBO campaign
          </p>
          <p className="text-sm mt-1">
            The optimization goal you pick here will{' '}
            <strong>lock the goal for every future ad set</strong> in this
            campaign — Meta requires every ad set in a CBO campaign to share
            the same optimization goal. Pick carefully; this can&apos;t be
            changed later without recreating the campaign.
          </p>
        </Alert>
      )}

      <div>
        <label
          htmlFor="meta-ad-set-name"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          Ad set name <span className="text-red-500" aria-label="required">*</span>
        </label>
        <Input
          id="meta-ad-set-name"
          type="text"
          value={adSetName}
          onChange={(e) => onChangeName(e.target.value)}
          placeholder="e.g. US Mobile — Interests in running"
          maxLength={400}
          aria-invalid={Boolean(errors?.name)}
          aria-describedby={errors?.name ? 'meta-ad-set-name-error' : undefined}
          required
        />
        {errors?.name && (
          <p
            id="meta-ad-set-name-error"
            role="alert"
            className="text-sm text-red-600 mt-1"
          >
            {errors.name}
          </p>
        )}
      </div>

      <div>
        <label
          htmlFor="meta-optimization-goal"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          Optimization goal <span className="text-red-500" aria-label="required">*</span>
        </label>
        <select
          id="meta-optimization-goal"
          value={optimizationGoal}
          disabled={goalLocked}
          onChange={(e) =>
            onChangeOptimizationGoal(e.target.value as MetaOptimizationGoal)
          }
          className={
            goalLocked
              ? 'block w-full rounded-md border border-gray-300 bg-gray-100 text-gray-700 cursor-not-allowed px-3 py-2 text-sm'
              : 'block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
          }
          aria-invalid={Boolean(errors?.optimizationGoal)}
          aria-describedby={
            errors?.optimizationGoal
              ? 'meta-optimization-goal-error'
              : 'meta-optimization-goal-hint'
          }
        >
          {allowedGoals.map((goal) => (
            <option key={goal} value={goal}>
              {OPTIMIZATION_GOAL_LABELS[goal]}
            </option>
          ))}
        </select>
        <p
          id="meta-optimization-goal-hint"
          className="text-xs text-gray-500 mt-1"
        >
          {goalLocked ? (
            <>
              Locked to{' '}
              <strong>{OPTIMIZATION_GOAL_LABELS[optimizationGoal]}</strong> —
              parent campaign uses CBO, and Meta requires every ad set under
              it to share the same optimization goal.
            </>
          ) : isFirstAdSetInCbo ? (
            <>
              Options narrowed by parent objective ({parentObjective}).{' '}
              <strong>First ad set:</strong> the parent campaign uses CBO,
              so this choice locks the goal for every future ad set in
              the campaign.
            </>
          ) : (
            <>Options narrowed by parent campaign's objective ({parentObjective}).</>
          )}
        </p>
        {errors?.optimizationGoal && (
          <p
            id="meta-optimization-goal-error"
            role="alert"
            className="text-sm text-red-600 mt-1"
          >
            {errors.optimizationGoal}
          </p>
        )}
      </div>

      <div>
        <label
          htmlFor="meta-billing-event"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          Billing event <span className="text-red-500" aria-label="required">*</span>
        </label>
        <select
          id="meta-billing-event"
          value={billingEvent}
          onChange={(e) => onChangeBillingEvent(e.target.value as MetaBillingEvent)}
          className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          aria-invalid={Boolean(errors?.billingEvent)}
          aria-describedby={
            errors?.billingEvent
              ? 'meta-billing-event-error'
              : 'meta-billing-event-hint'
          }
        >
          {allowedBillingEvents.map((event) => (
            <option key={event} value={event}>
              {BILLING_EVENT_LABELS[event]}
            </option>
          ))}
        </select>
        <p
          id="meta-billing-event-hint"
          className="text-xs text-gray-500 mt-1"
        >
          What Meta charges per. Options narrowed by selected goal.
        </p>
        {errors?.billingEvent && (
          <p
            id="meta-billing-event-error"
            role="alert"
            className="text-sm text-red-600 mt-1"
          >
            {errors.billingEvent}
          </p>
        )}
      </div>

      <div>
        <p className="block text-sm font-medium text-gray-700 mb-2">
          Initial status
        </p>
        <p className="text-xs text-gray-500 mb-2">
          PAUSED is the safer default. An ad set without ads won't serve
          regardless, so the choice has no operational impact until
          creatives land on top.
        </p>
        <div role="radiogroup" aria-label="Initial status" className="flex gap-2">
          {(['PAUSED', 'ACTIVE'] as const).map((s) => (
            <button
              key={s}
              type="button"
              role="radio"
              aria-checked={status === s}
              onClick={() => onChangeStatus(s)}
              className={cn(
                'flex-1 p-3 border-2 rounded-lg text-sm font-medium transition-all focus:outline-none focus:ring-2 focus:ring-blue-500',
                status === s
                  ? 'border-blue-600 bg-blue-50 text-blue-900'
                  : 'border-gray-200 hover:bg-gray-50 bg-white text-gray-700',
              )}
            >
              {s === 'PAUSED' ? 'Start paused' : 'Start active'}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};
